// src/api/apiSlice.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const meetingQueryParams = ({ meetingId, organizerId }) => {
  const params = new URLSearchParams({ meetingId });

  if (organizerId) {
    params.set("organizerId", organizerId);
  }

  return params.toString();
};

// Create an API slice
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: apiUrl,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("Content-type", "application/json");
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Add a note to the meeting at a specific time
    addNote: builder.mutation({
      query: ({ meetingId, note, time }) => ({
        url: `/seller/teams/addNote?meetingId=${encodeURIComponent(
          meetingId
        )}`, // meetingId in query param
        method: "POST",
        body: {
          notes: note,
          noteAt: time,
        },
      }),
    }),
    // Live per-participant talking ratio (polled during the meeting)
    getLiveTalkingRatio: builder.query({
      query: ({ meetingId, organizerId }) =>
        `/seller/teams/getLiveTalkingRatio?${meetingQueryParams({
          meetingId,
          organizerId,
        })}`,
    }),
    // Retrieve meeting agenda items
    getMeetingAgenda: builder.query({
      query: ({ meetingId, organizerId }) =>
        `/seller/teams/getMeetingAgenda?${meetingQueryParams({
          meetingId,
          organizerId,
        })}`,
    }),
    // Retrieve cue cards for the meeting customer
    getCueCards: builder.query({
      query: ({ meetingId, organizerId }) =>
        `/seller/teams/getCuecards?${meetingQueryParams({
          meetingId,
          organizerId,
        })}`,
    }),
    // Cue cards auto-matched against the meeting transcript (post-meeting, via
    // Microsoft Graph). Returns empty during a live meeting.
    getAutoCuecards: builder.query({
      query: ({ meetingId, organizerId }) =>
        `/seller/teams/getAutoCuecards?${meetingQueryParams({
          meetingId,
          organizerId,
        })}`,
    }),
    // ---- Live assist (Recall.ai bot) --------------------------------------
    // Send a Recall bot into the meeting for live transcription.
    startLiveAssist: builder.mutation({
      query: ({ meetingId, joinUrl, organizerId }) => {
        const params = new URLSearchParams({ meetingId, joinUrl });
        if (organizerId) {
          params.set("organizerId", organizerId);
        }
        return {
          url: `/seller/teams/startLiveAssist?${params.toString()}`,
          method: "POST",
        };
      },
    }),
    stopLiveAssist: builder.mutation({
      query: ({ meetingId }) => ({
        url: `/seller/teams/stopLiveAssist?meetingId=${encodeURIComponent(
          meetingId
        )}`,
        method: "POST",
      }),
    }),
    // Cue cards surfaced live by the bot during the call.
    getLiveCuecards: builder.query({
      query: ({ meetingId }) =>
        `/seller/teams/getLiveCuecards?meetingId=${encodeURIComponent(
          meetingId
        )}`,
    }),
    // Whether a live-assist bot session is active (to restore UI after reload).
    getLiveAssistStatus: builder.query({
      query: ({ meetingId }) =>
        `/seller/teams/getLiveAssistStatus?meetingId=${encodeURIComponent(
          meetingId
        )}`,
    }),
    // Add a new agenda item to the meeting
    addMeetingAgenda: builder.mutation({
      query: ({ meetingId, agendaText }) => ({
        url: `/seller/teams/addMeetingAgenda?meetingId=${encodeURIComponent(
          meetingId
        )}`,
        method: "POST",
        body: {
          agendaText,
        },
      }),
    }),
    // Update the status of the agenda item
    updateMeetingAgendaStatus: builder.mutation({
      query: ({ meetingId, meetingAgendaId, status }) => ({
        url: `/seller/teams/updateMeetingAgendaStatus?meetingId=${encodeURIComponent(
          meetingId
        )}`,
        method: "PUT",
        body: {
          meetingAgendaId,
          status,
        },
      }),
    }),
    // Start highlighting the meeting from the specified time
    startHighlighting: builder.mutation({
      query: ({ meetingId, time }) => ({
        url: `/seller/teams/startHighlighting?meetingId=${encodeURIComponent(
          meetingId
        )}&beginTime=${encodeURIComponent(time)}`,
        method: "POST",
      }),
    }),

    // Stop highlighting the meeting at a given time
    stopHighlighting: builder.mutation({
      query: ({ meetingId, time }) => ({
        url: `/seller/teams/stopHighlighting?meetingId=${encodeURIComponent(
          meetingId
        )}&endTime=${encodeURIComponent(time)}`,
        method: "PUT",
      }),
    }),
  }),
});

// Export hooks generated by createApi
export const {
  useAddMeetingAgendaMutation,
  useAddNoteMutation,
  useGetLiveTalkingRatioQuery,
  useGetMeetingQuery,
  useGetMeetingAgendaQuery,
  useGetCueCardsQuery,
  useGetNewRefreshTokenMutation,
  useGetNotesQuery,
  useSaveTokenMutation,
  useStartHighlightingMutation,
  useStopHighlightingMutation,
  useUpdateMeetingAgendaStatusMutation,
  useGetAutoCuecardsQuery,
  useStartLiveAssistMutation,
  useStopLiveAssistMutation,
  useGetLiveCuecardsQuery,
  useGetLiveAssistStatusQuery,
} = apiSlice;
